import { Button, Group, Input, Stack, Text } from '@mantine/core';
import { closeAllModals } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBot, DictionaryItem, useDictionaryMethods } from '@mygpt/app';

export const DictionaryModal = ({
  item,
  onSave,
  type,
}: {
  type: 'create' | 'edit';
  onSave: () => void;
  item?: DictionaryItem;
}) => {
  const { t } = useTranslation();
  const { botId } = useBot();
  const { createDictionary, loading, updateDictionary } = useDictionaryMethods();

  const [sourceValue, setSourceValue] = useState<string>('');
  const [targetValue, setTargetValue] = useState<string>('');

  useEffect(() => {
    if (item) {
      setSourceValue(item.source);
      setTargetValue(item.target);
    }
  }, []);

  const handleSave = async () => {
    const source = sourceValue.trim();
    const target = targetValue.trim();
    if (!source || !target) return;

    try {
      if (type === 'create') {
        await createDictionary({
          aiId: botId,
          source,
          target,
        });
      } else {
        if (!item) return;
        await updateDictionary({
          aiId: botId,
          dictionaryId: item.id,
          source,
          target,
        });
      }
      handleClose();
      onSave();
    } catch (error: any) {
      // source/target 重复等后端 400 需向用户提示，并保持弹窗打开、保留输入供修正
      const detail = error?.response?.data?.detail;
      showNotification({
        color: 'red',
        message: detail || t('errors.common'),
      });
    }
  };

  const handleClose = () => {
    setSourceValue('');
    setTargetValue('');
    closeAllModals();
  };

  const saveDisabled = useMemo(() => {
    return !sourceValue.trim() || !targetValue.trim();
  }, [sourceValue, targetValue]);

  return (
    <Stack>
      <Text ta="center">
        {type === 'create' ? t('dictionary.modal.create_title') : t('dictionary.modal.edit_title')}
      </Text>
      <Input
        placeholder={t('dictionary.modal.source_placeholder')}
        value={sourceValue}
        onChange={(e) => setSourceValue(e.target.value)}
        rightSection={
          <Group className="text-xs text-gray-400" wrap="nowrap">
            {`${sourceValue.length}`}/50
          </Group>
        }
        rightSectionWidth={50}
        maxLength={50}
      />
      <Input
        placeholder={t('dictionary.modal.target_placeholder')}
        value={targetValue}
        onChange={(e) => setTargetValue(e.target.value)}
        rightSection={
          <Group className="text-xs text-gray-400" wrap="nowrap">
            {`${targetValue.length}`}/200
          </Group>
        }
        rightSectionWidth={60}
        maxLength={200}
      />
      <Group justify="flex-end">
        <Button loading={loading} onClick={handleSave} disabled={saveDisabled}>
          {t('save')}
        </Button>
      </Group>
    </Stack>
  );
};
